import React, { useState } from 'react';
import {
  Button,
  Chip,
  CircularProgress,
  makeStyles,
  Typography,
} from '@material-ui/core';
import Dropzone from './dropzone';
import {
  uploadJd,
  uploadResume,
} from 'controllers/talentAiMatching/uploadFiles';
import { rankTalent } from 'controllers/talentAiMatching/rankTalent';
import { setFlag } from 'controllers/flags';

const useStyles = makeStyles(() => ({
  form: {
    padding: 24,
  },
  formSection: {
    background: '#fff',
    borderRadius: 2,
    marginTop: 24,
  },
  dropZoneContainer: {
    height: 120,
    width: '100%',
    border: '1px dashed #4b8c6f',
  },
  title: {
    opacity: 0.6,
    fontSize: '16px',
    fontWeight: 'normal',
    lineHeight: 1.31,
    letterSpacing: '0.16px',
    color: '#000000',
    marginBottom: 21,
  },
  btnsContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 24,
  },
  btns: {
    width: 144,
    marginRight: 10,
  },
  progress: {
    marginLeft: 8,
  },
  chip: {
    maxWidth: 130,
    marginRight: 7,
  },
  buttonTab: {
    marginRight: 8,
    fontFamily: 'Roboto',
    fontSize: 14,
    fontWeight: 'normal',
    fontStyle: 'normal',
    letterSpacing: 0.25,
  },
}));

const SemanticMatchForm = ({
  setTalentList,
  talentList = [],
  updateCredits,
}) => {
  const classes = useStyles();
  const [jdList, setJdList] = useState([]);
  const [resumeList, setResumeList] = useState([]);
  const [isJdUploading, setIsJdUploading] = useState(false);
  const [isResumeUploading, setIsResumeUploading] = useState(false);
  const [isRanking, setIsRanking] = useState(false);
  const [selectedJob, setSelectedJob] = useState({});

  const handleDrop = (func, key) => async (files = [], errorFiles = []) => {
    if (errorFiles.length) {
      setFlag('TalentAiFileTypeError', true);
    }

    if (files.length && key === 'Jd') {
      setIsJdUploading(true);

      const response = await func(files);

      setJdList(response);
      setIsJdUploading(false);
    } else if (files.length && key === 'Resume') {
      setIsResumeUploading(true);

      const response = await func(files);

      setResumeList((prevState) => [...prevState, ...response]);
      setIsResumeUploading(false);
    } else {
      return false;
    }
  };

  const onReset = () => {
    setJdList([]);
    setResumeList([]);
    setTalentList([]);
    setSelectedJob({});
  };

  const onRank = async () => {
    setIsRanking(true);
    const response = await rankTalent(jdList, resumeList, selectedJob);

    setTalentList(response);
    setIsRanking(false);
    updateCredits();
  };

  const onDeleteJd = () => {
    setJdList([]);
  };

  const onDeleteResume = (deletedDocId) => () => {
    setResumeList((prevState) =>
      prevState.filter(({ docId }) => docId !== deletedDocId)
    );
  };

  return (
    <>
      <div className={classes.formSection}>
        <div className={classes.form}>
          <Typography classes={{ root: classes.title }}>Upload a JD</Typography>
          <Dropzone
            text={'Drag and drop JD here'}
            handleDrop={handleDrop(uploadJd, 'Jd')}
            isLoading={isJdUploading}
            multiple={false}
            disabled={selectedJob && selectedJob.jobId}
          />
          {jdList.map(({ originalFileName, docId }) => (
            <Chip
              classes={{ root: classes.chip }}
              title={originalFileName}
              key={docId}
              label={originalFileName}
              onDelete={onDeleteJd}
              color="default"
              variant="outlined"
            />
          ))}
        </div>
      </div>
      <div className={classes.formSection}>
        <div className={classes.form}>
          <Typography classes={{ root: classes.title }}>
            Upload Resumes (Accepts doc, docx, pdf, txt and zip)
          </Typography>
          <Dropzone
            text={'Drag and drop Resumes here'}
            handleDrop={handleDrop(uploadResume, 'Resume')}
            isLoading={isResumeUploading}
          />
          {resumeList.slice(0, 3).map(({ originalFileName, docId }) => (
            <Chip
              classes={{ root: classes.chip }}
              title={originalFileName}
              key={docId}
              label={originalFileName}
              onDelete={onDeleteResume(docId)}
              color="default"
              variant="outlined"
            />
          ))}
          {resumeList.length > 3 ? (
            <Chip
              classes={{ root: classes.chip }}
              label={`+ ${resumeList.length - 3} More`}
              color="default"
              variant="outlined"
            />
          ) : null}
        </div>
      </div>
      <div className={classes.btnsContainer}>
        <Button
          variant="outlined"
          color="default"
          disabled={!jdList.length && !resumeList.length && !talentList.length}
          classes={{ root: classes.btns }}
          onClick={onReset}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={
            (!jdList.length && !selectedJob.jobId) ||
            !resumeList.length ||
            isRanking
          }
          classes={{ root: classes.btns }}
          onClick={onRank}
        >
          Rank
          {isRanking ? (
            <CircularProgress
              size={16}
              color="primary"
              classes={{ root: classes.progress }}
            />
          ) : null}
        </Button>
      </div>
    </>
  );
};

export default SemanticMatchForm;
